<script setup lang="ts">
import { ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { GuestService } from '../../api/guest/guestService';
import { ButtonSeverity } from '../../presets/button/types/buttonServerity';
import { UserRole } from '@common/contracts';
import { useUserStore } from '../../stores/userStore/userStore';
import BaseButton from '../BaseButton/BaseButton.vue';
import SharePhotoZodSchema from '@/pages/Schemas/SharePhotoZodSchema';
import { validateSchema } from '@/pages/Schemas/Schemas';

const props = defineProps({
  dialogVisible: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits<{
  hide: [];
  emailsSent: [];
  error: [message: string];
}>();

const sharePhotoFormValidationSchema = validateSchema(SharePhotoZodSchema);

const { handleSubmit, errors, resetForm } = useForm<{
  email: string;
}>({
  validationSchema: sharePhotoFormValidationSchema,
  validateOnMount: false,
});

const { value: email } = useField<string>('email');

const guests = ref<Record<string, string | number>[]>([]);
const emails = ref<string[]>([]);
const selectedEmails = ref<string[]>([]);
const loading = ref(false);
const showSuccess = ref(false);
const showError = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const fallbackCopyTextToClipboard = (text: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
    showSuccess.value = true;
    successMessage.value = 'Link został skopiowany';
  } catch (err) {
    console.error('Fallback failed', err);
    showError.value = true;
    errorMessage.value = 'Nie udało się skopiować linku';
  }

  document.body.removeChild(textArea);
};

const copyLinkToClipboard = async (): Promise<void> => {
  try {
    loading.value = true;
    const link = await GuestService.createShareLink();

    try {
      await navigator.clipboard.writeText(link);
      showSuccess.value = true;
      successMessage.value = 'Link został skopiowany do schowka';
    } catch (_err) {
      console.warn('Clipboard API failed, using fallback');
      fallbackCopyTextToClipboard(link);
    }

    setTimeout(() => {
      showSuccess.value = false;
      successMessage.value = '';
    }, 3000);
  } catch (error) {
    console.error('Error copying link:', error);
    showError.value = true;
    errorMessage.value = 'Wystąpił błąd podczas kopiowania linku';
    setTimeout(() => {
      showError.value = false;
      errorMessage.value = '';
    }, 3000);
  } finally {
    loading.value = false;
  }
};

const onSubmit = handleSubmit(async (values) => {
  try {
    loading.value = true;
    const { email } = values;
    await GuestService.sendShareLinks([email]);
    showSuccess.value = true;
    successMessage.value = 'Linki zostały wysłane';
    setTimeout(() => {
      showSuccess.value = false;
      successMessage.value = '';
    }, 3000);
    resetForm();
  } catch (error) {
    console.error('Error sending share links:', error);
    showError.value = true;
    errorMessage.value = 'Wystąpił błąd podczas wysyłania linków';
    setTimeout(() => {
      showError.value = false;
      errorMessage.value = '';
    }, 3000);
  } finally {
    loading.value = false;
    emit('hide');
  }
});

const handleSelectedEmailsSubmit = async (): Promise<void> => {
  if (selectedEmails.value.length === 0) return;

  try {
    loading.value = true;
    await GuestService.sendShareLinks(selectedEmails.value);
    selectedEmails.value = [];
    showSuccess.value = true;
    successMessage.value = 'Linki zostały wysłane';
    setTimeout(() => {
      showSuccess.value = false;
      successMessage.value = '';
    }, 3000);
  } catch (error) {
    console.error('Error sending share links:', error);
    showError.value = true;
    errorMessage.value = 'Wystąpił błąd podczas wysyłania linków';
    setTimeout(() => {
      showError.value = false;
      errorMessage.value = '';
    }, 3000);
  } finally {
    loading.value = false;
    emit('hide');
  }
};

watch(
  () => props.dialogVisible,
  async (isVisible) => {
    if (!isVisible) return;

    const userStore = useUserStore();
    const userRole = ref(userStore.getLoggedInUserRole);

    if (userRole.value === UserRole.guest) {
      return;
    }

    try {
      const guestsResponse = await GuestService.getGuestsData();
      guests.value = guestsResponse.data as unknown as Record<string, string | number>[];
      emails.value = Array.from(
        new Set(
          guests.value
            .filter((guest) => guest['email'] != null && guest['email'] !== '')
            .map((guest) => guest['email']?.toString() ?? ''),
        ),
      );
    } catch (error) {
      console.error('Error fetching guests data:', error);
      showError.value = true;
      errorMessage.value = 'Wystąpił błąd podczas pobierania danych gości';
      setTimeout(() => {
        showError.value = false;
        errorMessage.value = '';
      }, 3000);
    }
  },
);
</script>

<template>
  <div
    v-if="dialogVisible"
    class="share-dialog"
  >
    <div
      class="share-dialog__overlay"
      @click="emit('hide')"
    ></div>
    <div class="share-dialog__content">
      <div class="share-dialog__header">
        <h2 class="share-dialog__title">Udostępnij zdjęcia swoim gościom</h2>
        <button
          class="share-dialog__close-button"
          @click="$emit('hide')"
        >
          <span class="share-dialog__close-icon">×</span>
        </button>
      </div>

      <div class="share-dialog__body">
        <form
          class="share-dialog__form"
          @submit="onSubmit"
        >
          <div class="share-dialog__main-section">
            <div
              v-if="selectedEmails.length === 0"
              class="share-dialog__email-input"
            >
              <div class="share-dialog__float-label">
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  class="share-dialog__input"
                  :class="{ 'share-dialog__input--error': errors.email }"
                  :disabled="loading"
                />
                <label
                  class="share-dialog__label"
                  for="email"
                  >Email</label
                >
              </div>
              <small class="share-dialog__error">{{ errors.email || '&nbsp;' }}</small>
            </div>

            <div
              v-if="emails.length > 0 && email.length === 0"
              class="share-dialog__multiselect"
            >
              <div class="share-dialog__multiselect-input">
                <div class="share-dialog__selected-emails">
                  <span
                    v-for="selectedEmail in selectedEmails"
                    :key="selectedEmail"
                    class="share-dialog__email-chip"
                  >
                    {{ selectedEmail }}
                    <button
                      class="share-dialog__remove-email"
                      @click="selectedEmails = selectedEmails.filter((e) => e !== selectedEmail)"
                    >
                      ×
                    </button>
                  </span>
                </div>
                <select
                  v-model="selectedEmails"
                  multiple
                  class="share-dialog__select"
                  :disabled="loading"
                >
                  <option
                    v-for="emailOption in emails"
                    :key="emailOption"
                    :value="emailOption"
                    :selected="selectedEmails.includes(emailOption)"
                  >
                    {{ emailOption }}
                  </option>
                </select>
              </div>
            </div>

            <BaseButton
              :button-text="'Wyślij'"
              icon="pi-share-alt"
              class="share-dialog__submit-button"
              :severity="ButtonSeverity.primary"
              type="submit"
              :loading="loading"
              @clicked="handleSelectedEmailsSubmit"
            />
          </div>

          <div class="share-dialog__divider">
            <span class="share-dialog__divider-text">lub udostępnij za pomocą linka:</span>
            <BaseButton
              :button-text="'Kopiuj link do schowka'"
              :ignore-icon="true"
              icon="pi-clipboard"
              class="share-dialog__copy-button"
              :severity="ButtonSeverity.primary"
              :loading="loading"
              @clicked="copyLinkToClipboard"
            />
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="showSuccess"
      class="share-dialog__message share-dialog__message--success"
    >
      {{ successMessage }}
    </div>

    <div
      v-if="showError"
      class="share-dialog__message share-dialog__message--error"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.share-dialog {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  &__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba($black, 0.5);
    backdrop-filter: blur(4px);
  }

  &__content {
    position: relative;
    width: 90%;
    max-width: 80rem;
    max-height: 90vh;
    background-color: $white-200;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba($black-100, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    padding: 2rem 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid $brown-300;
    background-color: $white-200;
  }

  &__title {
    font-size: 2.25rem;
    color: $brown-200;
    font-weight: 600;
    margin: 0;

    @media (min-width: $media-min-tablet) {
      font-size: 3rem;
    }
  }

  &__close-button {
    background: none;
    border: none;
    color: $brown-200;
    font-size: 2rem;
    cursor: pointer;
    padding: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      color: $brown-100;
      transform: scale(1.1);
    }
  }

  &__close-icon {
    font-size: 2.5rem;
    line-height: 1;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__main-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__email-input {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  &__float-label {
    position: relative;
    width: 100%;
  }

  &__input {
    width: 100%;
    padding: 1rem 2rem;
    border: 1px solid $brown-300;
    border-radius: 0.5rem;
    font-size: 1.5rem;
    transition: all 0.2s ease;
    background-color: $white;

    &:focus {
      outline: none;
      border-color: $brown-100;
      box-shadow: 0 0 0 2px rgba($brown-100, 0.1);
    }

    &:disabled {
      background-color: $brown-600;
      cursor: not-allowed;
    }

    &--error {
      border-color: $brown-200;
    }
  }

  &__label {
    position: absolute;
    top: -0.5rem;
    left: 1rem;
    background-color: $white;
    padding: 0 0.5rem;
    color: $brown-300;
    font-size: 1.2rem;
  }

  &__error {
    color: $brown-200;
    font-size: 1.2rem;
  }

  &__multiselect {
    width: 100%;
  }

  &__multiselect-input {
    position: relative;
    width: 100%;
    min-height: 4rem;
    border: 1px solid $brown-300;
    border-radius: 0.5rem;
    background-color: $white;
    transition: all 0.2s ease;
    padding: 0.5rem;

    &:hover {
      border-color: $brown-100;
    }

    &:focus-within {
      border-color: $brown-100;
      box-shadow: 0 0 0 2px rgba($brown-100, 0.1);
    }
  }

  &__selected-emails {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__email-chip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    background-color: $brown-100;
    color: $white;
    border-radius: 0.25rem;
    font-size: 1.2rem;
    box-shadow: 0 2px 4px rgba($black-100, 0.05);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 8px rgba($black-100, 0.1);
    }
  }

  &__remove-email {
    background: none;
    border: none;
    color: $white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  &__select {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  &__submit-button,
  &__copy-button {
    width: 100%;
    margin-top: 1rem;
    box-shadow: 0 2px 4px rgba($black-100, 0.1);
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba($black-100, 0.15);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba($black-100, 0.1);
    }
  }

  &__divider {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid $brown-300;
  }

  &__divider-text {
    font-family: Mate, serif;
    font-size: 1.5rem;
    color: $brown-100;
    text-align: center;
    margin-bottom: 0.5rem;
  }

  &__message {
    position: fixed;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    font-size: 1.5rem;
    animation: slide-up 0.3s ease-out;
    box-shadow: 0 4px 8px rgba($black-100, 0.1);
    backdrop-filter: blur(4px);

    &--success {
      background-color: rgb(34 197 94 / 90%);
      color: white;
      border: 1px solid rgb(22 163 74 / 30%);
    }

    &--error {
      background-color: rgba($brown-200, 0.9);
      color: white;
      border: 1px solid rgba($brown-200, 0.3);
    }
  }
}

@keyframes slide-up {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }

  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}
</style>
